import React, { useState, useEffect, useCallback } from 'react';
import type { UserRecord, UserRole } from '../../lib/types';
import { fetchUsers, grantAccess, updateUserRole, revokeUser } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

const ROLE_LABELS: Record<UserRole, string> = {
  pending: 'Pending',
  editor: 'Editor',
  admin: 'Admin',
};

const ROLE_COLORS: Record<UserRole, string> = {
  pending: 'bg-gray-100 text-gray-600',
  editor: 'bg-blue-100 text-blue-700',
  admin: 'bg-purple-100 text-purple-700',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const UserManager: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Grant form
  const [grantEmail, setGrantEmail] = useState('');
  const [grantRole, setGrantRole] = useState<UserRole>('editor');
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantSuccess, setGrantSuccess] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUsers();
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleGrant = async () => {
    if (!grantEmail.trim()) return;
    setGranting(true);
    setGrantError(null);
    setGrantSuccess(null);
    try {
      await grantAccess(grantEmail.trim(), grantRole);
      setGrantSuccess(`Access granted to ${grantEmail}`);
      setGrantEmail('');
      await loadUsers();
    } catch (err) {
      setGrantError(err instanceof Error ? err.message : 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const handleRoleChange = async (email: string, newRole: UserRole) => {
    try {
      await updateUserRole(email, newRole);
      setUsers((prev) =>
        prev.map((u) => (u.email === email ? { ...u, role: newRole } : u))
      );
    } catch (err) {
      alert(`Failed to update role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleRevoke = async (email: string) => {
    if (!confirm(`Remove ${email} from the system? They will need to log in again.`)) return;
    try {
      await revokeUser(email);
      setUsers((prev) => prev.filter((u) => u.email !== email));
    } catch (err) {
      alert(`Failed to revoke: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">User Management</h1>

      {/* Grant Access Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-8 shadow-sm">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Grant Access</h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="user@tddt.org"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleGrant()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={grantRole}
              onChange={(e) => setGrantRole(e.target.value as UserRole)}
              className="border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            onClick={handleGrant}
            disabled={granting || !grantEmail.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {granting ? 'Granting...' : 'Grant Access'}
          </button>
        </div>
        {grantError && (
          <p className="mt-2 text-sm text-red-600">{grantError}</p>
        )}
        {grantSuccess && (
          <p className="mt-2 text-sm text-green-600">{grantSuccess}</p>
        )}
      </div>

      {/* Users Table */}
      {error ? (
        <div className="text-red-600 text-sm bg-red-50 px-4 py-3 rounded">{error}</div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-500">Loading users...</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-700">User</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Role</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Granted By</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Last Seen</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-400">
                    No users yet. Users appear here after their first login.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.email} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.picture && (
                          <img
                            src={u.picture}
                            alt=""
                            className="w-7 h-7 rounded-full"
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{u.name}</div>
                          <div className="text-gray-500 text-xs">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.email === currentUser?.email ? (
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role]}`}
                        >
                          {ROLE_LABELS[u.role]} (you)
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          onChange={(e) =>
                            handleRoleChange(u.email, e.target.value as UserRole)
                          }
                          className={`text-xs font-medium px-2 py-0.5 rounded border-0 ${ROLE_COLORS[u.role]} focus:outline-none cursor-pointer`}
                        >
                          <option value="pending">Pending</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {u.granted_by || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(u.last_seen)}
                    </td>
                    <td className="px-4 py-3">
                      {u.email !== currentUser?.email && (
                        <button
                          onClick={() => handleRevoke(u.email)}
                          className="text-xs text-red-600 hover:text-red-800 hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
